import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import {
  getAuth,
  getIdToken as getFirebaseIdToken,
  GoogleAuthProvider,
  OAuthProvider,
  revokeAccessToken as revokeFirebaseAccessToken,
  signInWithPopup,
  signOut as signOutFirebase,
} from "firebase/auth";
import { app } from "../firebase";

const SUPPORTED_PROVIDERS = new Set(["google.com", "apple.com"]);
const webAuth = getAuth(app);
const GOOGLE_RETRY_DELAYS = [180, 420];

// Android'de bir hesap secici acikken ikinci kez baslatilmasi bazi OEM
// surumlerinde "headful operation is already in progress" hatasi uretir.
// Ayni anda gelen istekleri tek native akista birlestiriyoruz.
let activeAndroidGoogleRequest = null;

function assertProvider(provider) {
  const normalized = String(provider || "").trim();
  if (!SUPPORTED_PROVIDERS.has(normalized)) {
    const error = new Error("Unsupported social provider");
    error.code = "SOCIAL_PROVIDER_NOT_SUPPORTED";
    throw error;
  }
  return normalized;
}

function collectErrorText(error) {
  const parts = [];
  const add = (value) => {
    if (value === undefined || value === null) return;
    if (typeof value === "string" || typeof value === "number") {
      parts.push(String(value));
    }
  };

  add(error?.name);
  add(error?.code);
  add(error?.message);
  add(error?.errorMessage);
  add(error?.status);
  add(error?.data?.code);
  add(error?.data?.message);
  add(error?.cause?.name);
  add(error?.cause?.code);
  add(error?.cause?.message);

  return parts.join(" | ").toLowerCase();
}

function containsAny(text, values) {
  return values.some((value) => text.includes(value));
}

function isGoogleNoCredentialError(error) {
  const text = collectErrorText(error);
  return containsAny(text, [
    "nocredentialexception",
    "no credential",
    "no credentials",
    "no viable credential",
    "matching credential",
    "cannot find a matching credential",
    "28433",
    "begin sign in",
  ]);
}

function isCredentialProviderUnavailableError(error) {
  const text = collectErrorText(error);
  return containsAny(text, [
    "getcredentialproviderconfigurationexception",
    "no provider dependencies",
    "credential manager is not supported",
    "credential manager unavailable",
    "credentialmanager unavailable",
    "provider configuration",
    "provider unavailable",
    "unsupported credential",
  ]);
}

function isAuthenticationBusyError(error) {
  const text = collectErrorText(error);
  return containsAny(text, [
    "headful operation is already in progress",
    "operation already in progress",
    "another operation",
    "concurrent",
  ]);
}

function isCancellationError(error) {
  const text = collectErrorText(error);
  return containsAny(text, [
    "getcredentialcancellationexception",
    "user canceled",
    "user cancelled",
    "cancelled by user",
    "canceled by user",
    "popup-closed-by-user",
    "web-context-cancelled",
    "12501",
    "social authentication cancelled",
  ]);
}

function isNetworkAuthError(error) {
  const text = collectErrorText(error);
  return containsAny(text, [
    "network error",
    "network request failed",
    "network_error",
    "network unavailable",
    "unable to resolve host",
    "failed to connect",
    "timed out",
    "timeout",
  ]);
}

function isGoogleConfigurationError(error) {
  const text = collectErrorText(error);
  return containsAny(text, [
    "developer_error",
    "developer error",
    "api_exception: 10",
    "status code: 10",
    "oauth client",
    "invalid_client",
    "certificate",
    "sha-1",
    "sha1",
    "configuration error",
    "provider is not configured",
  ]);
}

function isGooglePlayServicesError(error) {
  const text = collectErrorText(error);
  return containsAny(text, [
    "google play services",
    "play services",
    "service_invalid",
    "service_version_update_required",
    "service_missing",
    "api_unavailable",
  ]);
}

function createNormalizedError(code, originalError) {
  const normalized = new Error(code);
  normalized.code = code;

  // Teknik ayrinti yalnizca gelistirme derlemesinde tutulur. Release
  // arayuzunde ham Google/Android hata metni kesinlikle gosterilmez.
  if (process.env.NODE_ENV !== "production") {
    normalized.debugMessage = collectErrorText(originalError);
  }
  return normalized;
}

function normalizeAuthError(error) {
  const existingCode = String(error?.code || "");
  if (existingCode.startsWith("SOCIAL_")) return error;

  // "16" kodu hem iptal hem de One Tap'te credential bulunamamasi icin
  // kullanilabildiginden once anlamli mesaj siniflari kontrol edilir.
  if (isGoogleNoCredentialError(error)) {
    return createNormalizedError("SOCIAL_GOOGLE_NO_CREDENTIAL", error);
  }
  if (isCancellationError(error)) {
    return createNormalizedError("SOCIAL_AUTH_CANCELLED", error);
  }
  if (isAuthenticationBusyError(error)) {
    return createNormalizedError("SOCIAL_AUTH_BUSY", error);
  }
  if (isNetworkAuthError(error)) {
    return createNormalizedError("SOCIAL_NETWORK_ERROR", error);
  }
  if (isGoogleConfigurationError(error)) {
    return createNormalizedError("SOCIAL_GOOGLE_CONFIGURATION", error);
  }
  if (isGooglePlayServicesError(error)) {
    return createNormalizedError("SOCIAL_GOOGLE_PLAY_SERVICES", error);
  }
  if (isCredentialProviderUnavailableError(error)) {
    return createNormalizedError("SOCIAL_GOOGLE_PROVIDER_UNAVAILABLE", error);
  }
  return createNormalizedError("SOCIAL_AUTH_FAILED", error);
}

function isNativePlatform() {
  return Capacitor.isNativePlatform();
}

function isAndroidNativePlatform() {
  return isNativePlatform() && Capacitor.getPlatform() === "android";
}

function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function debugGoogleAttempt(step, error) {
  if (process.env.NODE_ENV === "production") return;
  console.warn(`[GoogleAuth] ${step}`, collectErrorText(error));
}

function createWebProvider(provider, language) {
  if (provider === "google.com") {
    const googleProvider = new GoogleAuthProvider();
    googleProvider.setCustomParameters({ prompt: "select_account" });
    return googleProvider;
  }

  const appleProvider = new OAuthProvider("apple.com");
  appleProvider.addScope("email");
  appleProvider.addScope("name");
  appleProvider.setCustomParameters({
    locale: language === "en" ? "en_US" : "tr_TR",
  });
  return appleProvider;
}

async function recoverPendingAndroidAuthentication() {
  try {
    const pending = await FirebaseAuthentication.getPendingAuthResult();
    return pending?.user ? pending : null;
  } catch (_) {
    return null;
  }
}

function shouldTryAlternativeGoogleFlow(error) {
  return (
    isGoogleNoCredentialError(error) ||
    isCredentialProviderUnavailableError(error) ||
    isAuthenticationBusyError(error) ||
    isGooglePlayServicesError(error)
  );
}

async function runAndroidGoogleAuthentication() {
  // 1) Google'in guncel Credential Manager akisi.
  // 2) OEM/Play Services uyumsuzlugunda plugin'in One Tap akisi.
  // 3) One Tap ilk acilista hesap eslestiremezse CM bir kez daha denenir.
  // Her adim plugin'in resmi API'sini kullanir; iOS/web akislari etkilenmez.
  const attempts = [true, false, true];
  let lastError = null;

  for (let index = 0; index < attempts.length; index += 1) {
    const useCredentialManager = attempts[index];
    try {
      return await FirebaseAuthentication.signInWithGoogle({
        useCredentialManager,
      });
    } catch (error) {
      const pending = await recoverPendingAndroidAuthentication();
      if (pending) return pending;

      if (isCancellationError(error) && !isGoogleNoCredentialError(error)) {
        throw createNormalizedError("SOCIAL_AUTH_CANCELLED", error);
      }

      lastError = error;
      debugGoogleAttempt(
        useCredentialManager ? "credential-manager failed" : "one-tap failed",
        error
      );

      const hasNextAttempt = index < attempts.length - 1;
      if (!hasNextAttempt || !shouldTryAlternativeGoogleFlow(error)) break;
      await wait(GOOGLE_RETRY_DELAYS[Math.min(index, GOOGLE_RETRY_DELAYS.length - 1)]);
    }
  }

  throw normalizeAuthError(lastError);
}

async function startAndroidGoogleAuthentication() {
  if (activeAndroidGoogleRequest) return activeAndroidGoogleRequest;

  activeAndroidGoogleRequest = runAndroidGoogleAuthentication().finally(() => {
    activeAndroidGoogleRequest = null;
  });
  return activeAndroidGoogleRequest;
}

async function startNativeAuthentication(provider, language) {
  await FirebaseAuthentication.setLanguageCode({
    languageCode: language === "en" ? "en" : "tr",
  }).catch(() => {});

  let result;
  if (provider === "apple.com") {
    result = await FirebaseAuthentication.signInWithApple();
  } else if (isAndroidNativePlatform()) {
    result = await startAndroidGoogleAuthentication();
  } else {
    // iOS Google akisi daha once calisan plugin varsayilanini aynen korur.
    result = await FirebaseAuthentication.signInWithGoogle();
  }

  const tokenResult = await FirebaseAuthentication.getIdToken({ forceRefresh: true });
  const idToken = String(tokenResult?.token || "").trim();

  return {
    provider,
    idToken,
    user: result?.user || null,
    credential: result?.credential || null,
  };
}

async function startWebAuthentication(provider, language) {
  webAuth.languageCode = language === "en" ? "en" : "tr";

  // Safari requires the popup to be opened directly from the button gesture.
  const result = await signInWithPopup(
    webAuth,
    createWebProvider(provider, language)
  );
  const idToken = String(
    await getFirebaseIdToken(result.user, true)
  ).trim();
  const firebaseCredential = provider === "google.com"
    ? GoogleAuthProvider.credentialFromResult(result)
    : OAuthProvider.credentialFromResult(result);

  return {
    provider,
    idToken,
    user: result.user || null,
    credential: firebaseCredential
      ? {
          providerId: firebaseCredential.providerId,
          accessToken: firebaseCredential.accessToken || null,
          idToken: firebaseCredential.idToken || null,
        }
      : null,
  };
}

export async function startSocialAuthentication(provider, language = "tr") {
  const normalizedProvider = assertProvider(provider);
  try {
    const result = isNativePlatform()
      ? await startNativeAuthentication(normalizedProvider, language)
      : await startWebAuthentication(normalizedProvider, language);
    const idToken = String(result?.idToken || "").trim();
    if (!idToken) {
      const error = new Error("Firebase ID token missing");
      error.code = "SOCIAL_TOKEN_MISSING";
      throw error;
    }
    return result;
  } catch (error) {
    throw normalizeAuthError(error);
  }
}

export async function revokeAppleCredential(credential) {
  const token = String(
    credential?.authorizationCode ||
    credential?.accessToken ||
    ""
  ).trim();
  if (!token) {
    const error = new Error("Apple revocation token missing");
    error.code = "APPLE_REVOCATION_TOKEN_MISSING";
    throw error;
  }
  if (isNativePlatform()) {
    await FirebaseAuthentication.revokeAccessToken({ token });
    return;
  }
  await revokeFirebaseAccessToken(webAuth, token);
}

export async function getCurrentSocialIdToken() {
  const token = isNativePlatform()
    ? String(
        (await FirebaseAuthentication.getIdToken({ forceRefresh: true }))?.token || ""
      ).trim()
    : String(
        webAuth.currentUser
          ? await getFirebaseIdToken(webAuth.currentUser, true)
          : ""
      ).trim();
  if (!token) {
    const error = new Error("Firebase ID token missing");
    error.code = "SOCIAL_TOKEN_MISSING";
    throw error;
  }
  return token;
}

export async function signOutSocialAuthentication() {
  try {
    if (isNativePlatform()) {
      await FirebaseAuthentication.signOut();
    } else {
      await signOutFirebase(webAuth);
    }
  } catch (_) {}
}
